import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth';
import { useQueryClient } from '@tanstack/react-query';
import { modelKeys } from '@/features/models/hooks';
import { ACTION_ITEMS_KEY } from '@/features/actionItems/hooks';
import { logger } from './logger';

const API_URL = import.meta.env.VITE_API_URL || '';

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private onConnectionFailureCallback?: () => void;

  /**
   * Set callback for connection failure notification
   */
  setOnConnectionFailure(callback: () => void): void {
    this.onConnectionFailureCallback = callback;
  }

  /**
   * Initialize WebSocket connection with authentication
   */
  connect(queryClient: ReturnType<typeof useQueryClient>): void {
    const { accessToken, currentTenant } = useAuthStore.getState();

    // Don't connect if not authenticated or no tenant
    if (!accessToken || !currentTenant) {
      logger.debug('WebSocket: Skipping connection - not authenticated or no tenant');
      return;
    }

    // Already connected or connecting
    if (this.socket?.connected || this.socket?.active) {
      logger.debug('WebSocket: Already connected or connecting');
      return;
    }

    logger.info('WebSocket: Connecting...', { tenantId: currentTenant.tenantId });

    this.socket = io(API_URL, {
      auth: {
        token: accessToken,
        tenantId: currentTenant.tenantId,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    // Connection successful
    this.socket.on('connect', () => {
      logger.info('WebSocket: Connected', { socketId: this.socket?.id });
      this.reconnectAttempts = 0;
    });

    // Authentication error
    this.socket.on('connect_error', (error) => {
      logger.error('WebSocket: Connection error', { error: error.message });
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        logger.error('WebSocket: Max reconnection attempts reached');
        // Notify user of permanent connection failure
        if (this.onConnectionFailureCallback) {
          this.onConnectionFailureCallback();
        }
        this.disconnect();
      }
    });

    // Disconnected
    this.socket.on('disconnect', (reason) => {
      logger.info('WebSocket: Disconnected', { reason });
    });

    // Listen for link metadata extraction events
    this.socket.on('linkMetadataExtracted', (data) => {
      logger.debug('WebSocket: Link metadata extracted', {
        modelId: data.modelId,
        linkId: data.linkId,
      });

      // Invalidate queries to refresh UI with new metadata
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(data.modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
    });

    // Listen for file metadata extraction events
    this.socket.on('fileMetadataExtracted', (data: {
      modelId: string;
      fileId: string;
      status: 'COMPLETED' | 'FAILED';
      error?: string;
    }) => {
      logger.debug('WebSocket: File metadata extracted', {
        modelId: data.modelId,
        fileId: data.fileId,
        status: data.status,
      });

      // Invalidate queries to refresh UI with new metadata
      queryClient.invalidateQueries({ queryKey: modelKeys.detail(data.modelId) });
      queryClient.invalidateQueries({ queryKey: modelKeys.lists() });
      // Invalidate the file plates query to refresh the FileMetadataDisplay component
      queryClient.invalidateQueries({ queryKey: ['models', data.modelId, 'files', data.fileId, 'plates'] });
    });

    // Listen for action items updates
    this.socket.on('actionItemsUpdated', () => {
      logger.debug('WebSocket: Action items updated');

      // Invalidate action items query to refresh the list
      queryClient.invalidateQueries({ queryKey: [ACTION_ITEMS_KEY] });
    });
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      // Only log if we're actually connected or connecting
      if (this.socket.connected || this.socket.active) {
        logger.debug('WebSocket: Disconnecting...');
      }
      this.socket.disconnect();
      this.socket = null;
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const websocketService = new WebSocketService();
